import { type RefObject, useEffect, useRef } from 'react';
import { getFilesFromClipboardData } from 'v5/platform/dom/getFilesFromClipboardData';
import { isTypingTarget } from 'v5/platform/dom/isTypingTarget';

const ADDRESS = /^https?:\/\/\S+$/i;

/**
 * ⌘V anywhere on the page but in a field or a dialog that is not around `board`: a lone web address
 * goes to `onLink`, files to `onFiles` when given; any other text is left to whoever takes it.
 */
export function usePasteIntoBoard({
  enabled,
  board,
  onLink,
  onFiles
}: {
  enabled: boolean;
  board: RefObject<HTMLElement | null>;
  onLink(address: string): void;
  onFiles?(files: File[]): void;
}) {
  const latest = useRef({ onLink, onFiles });
  latest.current = { onLink, onFiles };
  useEffect(() => {
    if (!enabled) return;
    const onPaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      // a dialog over the page is not for the board, unless the board stands in it
      const dialog =
        event.target instanceof Element
          ? event.target.closest('[role="dialog"]')
          : null;
      if (dialog && !dialog.contains(board.current)) return;
      const files = getFilesFromClipboardData(event.clipboardData);
      if (files.length > 0) {
        if (!latest.current.onFiles) return;
        event.preventDefault();
        latest.current.onFiles(files);
        return;
      }
      const text = event.clipboardData?.getData('text/plain').trim() ?? '';
      if (!ADDRESS.test(text)) return;
      event.preventDefault();
      latest.current.onLink(text);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [enabled, board]);
}
